import { api } from '../../api';
import type { GetEdenArrayItem, GetEdenBody } from '../../type-utils';
import type { DimensionUnit, FbaShippingZone, StepLevel } from './easy-ship';

export type NumericValue = number | string;
export type SavedAmazonProduct = GetEdenArrayItem<
  (typeof api.api.api.v1)['amazon-products']['get']
>;
export type AmazonProductPayload = GetEdenBody<(typeof api.api.api.v1)['amazon-products']['post']>;

export interface CalculatorForm {
  sellingPrice: NumericValue;
  productCost: NumericValue;
  actualWeightGrams: NumericValue;
  lengthCm: NumericValue;
  widthCm: NumericValue;
  heightCm: NumericValue;
  packagingCost: NumericValue;
  costPerClick: NumericValue;
  customConversionRatePercentage: NumericValue;
  otherCost: NumericValue;
  returnRatePercentage: NumericValue;
  fbaStorageDays: NumericValue;
  fbaInboundCost: NumericValue;
}

export interface AmazonCalculatorState {
  name: string;
  stepLevel: StepLevel;
  fbaShippingZone: FbaShippingZone;
  fbaShipInOwnContainer: boolean;
  dimensionUnit: DimensionUnit;
  form: CalculatorForm;
}

export const initialCalculatorState: AmazonCalculatorState = {
  name: '',
  stepLevel: 'standard',
  fbaShippingZone: 'regional',
  fbaShipInOwnContainer: false,
  dimensionUnit: 'cm',
  form: {
    sellingPrice: '',
    productCost: '',
    actualWeightGrams: '',
    lengthCm: '',
    widthCm: '',
    heightCm: '',
    packagingCost: 0,
    costPerClick: 0,
    customConversionRatePercentage: 10,
    otherCost: 0,
    returnRatePercentage: 0,
    fbaStorageDays: 30,
    fbaInboundCost: 0,
  },
};

function numeric(value: NumericValue): number {
  return typeof value === 'number' ? value : Number(value);
}

export function amazonProductPayload(state: AmazonCalculatorState): AmazonProductPayload {
  return {
    name: state.name,
    stepLevel: state.stepLevel,
    fbaShippingZone: state.fbaShippingZone,
    fbaShipInOwnContainer: state.fbaShipInOwnContainer,
    dimensionUnit: state.dimensionUnit,
    sellingPrice: numeric(state.form.sellingPrice),
    productCost: numeric(state.form.productCost),
    actualWeightGrams: numeric(state.form.actualWeightGrams),
    lengthCm: numeric(state.form.lengthCm),
    widthCm: numeric(state.form.widthCm),
    heightCm: numeric(state.form.heightCm),
    packagingCost: numeric(state.form.packagingCost),
    costPerClick: numeric(state.form.costPerClick),
    customConversionRatePercentage: numeric(state.form.customConversionRatePercentage),
    otherCost: numeric(state.form.otherCost),
    returnRatePercentage: numeric(state.form.returnRatePercentage),
    fbaStorageDays: numeric(state.form.fbaStorageDays),
    fbaInboundCost: numeric(state.form.fbaInboundCost),
  };
}

export function calculatorStateFromProduct(product: SavedAmazonProduct): AmazonCalculatorState {
  return {
    name: product.name,
    stepLevel: product.stepLevel,
    fbaShippingZone: product.fbaShippingZone,
    fbaShipInOwnContainer: product.fbaShipInOwnContainer,
    dimensionUnit: product.dimensionUnit,
    form: {
      sellingPrice: product.sellingPrice,
      productCost: product.productCost,
      actualWeightGrams: product.actualWeightGrams,
      lengthCm: product.lengthCm,
      widthCm: product.widthCm,
      heightCm: product.heightCm,
      packagingCost: product.packagingCost,
      costPerClick: product.costPerClick,
      customConversionRatePercentage: product.customConversionRatePercentage,
      otherCost: product.otherCost,
      returnRatePercentage: product.returnRatePercentage,
      fbaStorageDays: product.fbaStorageDays,
      fbaInboundCost: product.fbaInboundCost,
    },
  };
}
